/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import {
  EuiButtonIcon,
  EuiCheckbox,
  EuiFormLabel,
  EuiPopover,
  EuiPopoverTitle,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { StackDeployment } from '@modules/ui-types'
import { useToggle } from '@modules/utils/hooks/useToggle'

import { deploymentHasFrozenTier } from '@/lib/stackDeployments/selectors/nodeRoles'
import { getVersion } from '@/lib/stackDeployments/selectors/fundamentals'
import { gte } from '@/lib/semver'

import { isRollingGrowAndShrinkForFrozen, strategies } from '../../lib/clusterStrategies'

import type { Strategy } from '../../lib/clusterStrategies'

type Props = {
  strategy: Strategy
  onUpdate: (strategy: Strategy) => void
  deployment: StackDeployment
  showConfigChangeStrategyForFrozen: boolean
}

function StrategyForFrozen({
  strategy,
  onUpdate,
  deployment,
  showConfigChangeStrategyForFrozen,
}: Props) {
  const hasFrozenTier = deploymentHasFrozenTier({ deployment })
  const version = getVersion({ deployment })
  const [isPopoverOpen, togglePopover, setPopoverOpen] = useToggle()

  // Don't render if deployment doesn't have frozen tier, feature flag is off, or version < 7.15
  if (!hasFrozenTier || !showConfigChangeStrategyForFrozen || !version || !gte(version, '7.15.0')) {
    return null
  }

  const isChecked = isRollingGrowAndShrinkForFrozen(strategy)

  return (
    <Fragment>
      <EuiFormLabel>
        <FormattedMessage
          id='deployment-configure-strategies.frozen.strategy-label'
          defaultMessage='Configuration Strategy'
        />
      </EuiFormLabel>
      <EuiSpacer size='s' />
      <EuiText size='s'>
        <FormattedMessage
          id='deployment-configure-strategies.frozen.strategy-description'
          defaultMessage='Control how configuration changes are applied to your deployment:'
        />
      </EuiText>

      <EuiSpacer size='s' />

      <EuiCheckbox
        id='strategy-frozen-grow-and-shrink'
        label={
          <Fragment>
            <FormattedMessage
              id='deployment-configure-strategies.frozen.frozen-grow-and-shrink-label'
              defaultMessage='Use rolling grow and shrink for frozen tier'
            />
            <EuiPopover
              anchorPosition='upCenter'
              button={
                <EuiButtonIcon
                  iconType='info'
                  color='primary'
                  aria-label='Info'
                  onClick={togglePopover}
                />
              }
              className='fs-unmask'
              isOpen={isPopoverOpen}
              closePopover={() => setPopoverOpen(false)}
            >
              <EuiPopoverTitle>
                <FormattedMessage
                  id='deployment-configure-strategies.frozen.popover-title'
                  defaultMessage='When should I use this?'
                />
              </EuiPopoverTitle>

              <div style={{ maxWidth: '300px' }}>
                <EuiText size='s'>
                  {' '}
                  <FormattedMessage
                    id='deployment-configure-strategies.frozen.popover-description'
                    defaultMessage="Use this option if you have 0 replicas in frozen tier and don't want any service interruption, but it may be slower than our original strategy."
                  />
                </EuiText>
              </div>
            </EuiPopover>
          </Fragment>
        }
        checked={isChecked}
        onChange={onChange}
      />

      <EuiSpacer size='m' />
    </Fragment>
  )

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    const shouldEnable = e.target.checked

    if (shouldEnable) {
      onUpdate(strategies.rollingGrowAndShrinkForFrozen)
    } else {
      // When unchecked, revert to autodetect strategy
      onUpdate(strategies.autodetect)
    }
  }
}

export default StrategyForFrozen
